import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Universal delete confirmation popup.
 * Renders via portal into document.body so it appears above sidebar.
 * @param {boolean} open - Whether popup is visible
 * @param {() => void} onClose - Called when closing (cross, No, or backdrop)
 * @param {() => void} onConfirm - Called when user clicks Yes (API integration later)
 * @param {string} entityLabel - e.g. "bin", "product", "inward transaction"
 * @param {string} [description] - Optional full message; overrides entityLabel-based text when set
 */
const DeletePopUp = ({
  open = false,
  onClose,
  onConfirm,
  entityLabel = 'item',
  description: descriptionProp,
}) => {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleYes = () => {
    if (onConfirm) onConfirm();
    onClose();
  };

  const handleNo = () => {
    onClose();
  };

  if (!open) return null;

  const label = entityLabel.trim() ? entityLabel : 'item';
  const description =
    descriptionProp?.trim() ||
    `Are you sure you want to delete this ${label}? This action cannot be undone.`;

  const popupContent = (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 99999 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-popup-title"
    >
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(16,24,40,0.35)' }}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      <div
        className="relative w-full bg-white overflow-hidden"
        style={{
          width: 520,
          maxWidth: '92vw',
          borderRadius: 18,
          padding: '22px 22px 18px',
          border: '1px solid #EEF1F5',
          boxShadow: '0 12px 30px rgba(16,24,40,0.14)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200"
          style={{ top: 16, right: 16, color: '#98A2B3' }}
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="flex items-start gap-3 pr-8">
          <div
            className="flex-shrink-0 flex items-center justify-center rounded-full"
            style={{ width: 44, height: 44, backgroundColor: 'rgba(255, 92, 120, 0.15)' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="#FF5C78" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          <div className="min-w-0">
            <h2 id="delete-popup-title" style={{ fontSize: 22, fontWeight: 600, color: '#101828', marginTop: 10, marginBottom: 0 }}>
              Delete Record ?
            </h2>
            <p style={{ fontSize: 14, color: '#98A2B3', lineHeight: 1.6, marginTop: 6, marginBottom: 0 }}>
              {description}
            </p>
          </div>
        </div>

        <div className="flex gap-4 mt-[18px]" style={{ marginTop: 18 }}>
          <button
            type="button"
            onClick={handleNo}
            className="flex-1 rounded-[10px] border font-semibold transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
            style={{
              height: 46,
              backgroundColor: '#FFFFFF',
              borderColor: '#D0D5DD',
              color: '#101828',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleYes}
            className="flex-1 rounded-[10px] border-0 font-semibold text-white transition hover:bg-[#F24B67] focus:outline-none focus:ring-2 focus:ring-red-200"
            style={{ height: 46, backgroundColor: '#FF5C78' }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(popupContent, document.body);
};

export default DeletePopUp;
